import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApiBase } from '../hooks/useApiBase';
import { listNotifications, getUnreadCount, markAsRead, deleteNotification } from '../api/notifications';
import type { Notification, NotificationListParams } from '../types/notifications';
import { usePolling } from '../hooks/usePolling';
import { useAuth } from './AuthContext';

const POLL_INTERVAL = 600000; // 10 minutes - lightweight badge check only

interface NotificationsContextValue {
  // Badge data (always available)
  hasUnread: boolean;
  unreadCount: number;
  
  // Full list data (only loaded when needed)
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  
  // Methods
  refresh: () => void;
  refreshUnreadCount: () => void;
  markAsRead: (notificationId: string, read?: boolean) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  
  // Lazy loading control
  requestFullList: (params?: NotificationListParams) => void;
  releaseFullList: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const apiBase = useApiBase();
  const { sessionToken } = useAuth();
  const sessionGenerationRef = useRef(0);

  useEffect(() => {
    sessionGenerationRef.current += 1;
  }, [sessionToken]);
  
  // Badge state (always loaded)
  const [hasUnread, setHasUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Full list state (lazy loaded)
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track which components are using full list
  const fullListSubscribersRef = useRef<number>(0);
  const [hasSubscribers, setHasSubscribers] = useState(false);
  const [currentParams, setCurrentParams] = useState<NotificationListParams>({});
  
  // Stable params key for dependency comparison - only serialize when params actually change
  const paramsKey = useMemo(() => {
    const keys = Object.keys(currentParams).sort();
    return keys.map(k => `${k}:${JSON.stringify(currentParams[k as keyof NotificationListParams])}`).join('|');
  }, [currentParams]);

  const checkUnreadCount = useCallback(async () => {
    const generation = sessionGenerationRef.current;
    if (!sessionToken) {
      setHasUnread(false);
      setUnreadCount(0);
      return;
    }
    try {
      const data = await getUnreadCount(apiBase);
      if (generation !== sessionGenerationRef.current) {
        return;
      }
      setHasUnread(data.has_unread);
      setUnreadCount(data.count);
    } catch (err) {
      // Silently fail - don't show error for polling (tests stub session)
      if (generation !== sessionGenerationRef.current) {
        return;
      }
    }
  }, [apiBase, sessionToken]);

  // Always poll unread count (lightweight badge check)
  usePolling(checkUnreadCount, POLL_INTERVAL, Boolean(sessionToken), Boolean(sessionToken));

  const loadNotifications = useCallback(async (params: NotificationListParams = {}) => {
    const generation = sessionGenerationRef.current;
    if (!sessionToken) {
      return;
    }
    try {
      if (generation === sessionGenerationRef.current) {
        setError(null);
        setLoading(true);
      }
      const data = await listNotifications(apiBase, params);
      if (generation === sessionGenerationRef.current) {
        setNotifications(data.items);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load notifications';
      if (generation === sessionGenerationRef.current) {
        setError(message);
      }
    } finally {
      if (generation === sessionGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [apiBase, sessionToken]);

  // Load full list when subscribers exist
  useEffect(() => {
    if (hasSubscribers && sessionToken) {
      loadNotifications(currentParams);
    }
  }, [paramsKey, hasSubscribers, sessionToken, loadNotifications]);

  useEffect(() => {
    if (!sessionToken) {
      fullListSubscribersRef.current = 0;
      setHasSubscribers(false);
      setNotifications([]);
      setHasUnread(false);
      setUnreadCount(0);
      setError(null);
      setLoading(false);
    }
  }, [sessionToken]);

  const handleMarkAsRead = useCallback(async (notificationId: string, read: boolean = true) => {
    const generation = sessionGenerationRef.current;
    try {
      const updated = await markAsRead(apiBase, notificationId, read);
      if (generation === sessionGenerationRef.current) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? updated : n)
        );
      }
      if (read) {
        if (generation === sessionGenerationRef.current) {
          setUnreadCount(prev => {
            const nextCount = Math.max(0, prev - 1);
            setHasUnread(nextCount > 0);
            return nextCount;
          });
        }
      } else {
        if (generation === sessionGenerationRef.current) {
          setUnreadCount(prev => {
            const nextCount = prev + 1;
            setHasUnread(true);
            return nextCount;
          });
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update notification';
      if (generation === sessionGenerationRef.current) {
        setError(message);
      }
      throw err;
    }
  }, [apiBase]);

  const handleDelete = useCallback(async (notificationId: string) => {
    const generation = sessionGenerationRef.current;
    try {
      // Get the notification before deleting to check if it was unread
      const notificationToDelete = notifications.find(n => n.id === notificationId);
      await deleteNotification(apiBase, notificationId);
      if (generation === sessionGenerationRef.current) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
      if (notificationToDelete && !notificationToDelete.read) {
        if (generation === sessionGenerationRef.current) {
          setUnreadCount(prev => {
            const nextCount = Math.max(0, prev - 1);
            setHasUnread(nextCount > 0);
            return nextCount;
          });
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete notification';
      if (generation === sessionGenerationRef.current) {
        setError(message);
      }
      throw err;
    }
  }, [apiBase, notifications]);

  const requestFullList = useCallback((params: NotificationListParams = {}) => {
    fullListSubscribersRef.current += 1;
    setHasSubscribers(true);
    setCurrentParams(params);
  }, []);

  const releaseFullList = useCallback(() => {
    fullListSubscribersRef.current = Math.max(0, fullListSubscribersRef.current - 1);
    if (fullListSubscribersRef.current === 0) {
      setHasSubscribers(false);
    }
  }, []);

  const value: NotificationsContextValue = useMemo(() => ({
    hasUnread,
    unreadCount,
    notifications,
    loading,
    error,
    refresh: () => loadNotifications(currentParams),
    refreshUnreadCount: checkUnreadCount,
    markAsRead: handleMarkAsRead,
    deleteNotification: handleDelete,
    requestFullList,
    releaseFullList,
  }), [
    hasUnread,
    unreadCount,
    notifications,
    loading,
    error,
    loadNotifications,
    currentParams,
    checkUnreadCount,
    handleMarkAsRead,
    handleDelete,
    requestFullList,
    releaseFullList,
  ]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotificationsContext must be used within NotificationsProvider');
  }
  return context;
}
